import {
  useUserCtxSelector,
  useManagerCtxSelector,
} from '../xstate/app.xstate';

export enum OrganizationRoleNames {
  SUPER_ADMIN = 'Super Admin',
  ADMIN = 'Organization Admin',
  MANAGER = 'Organization Manager',
}

/**
 * Roles that may see and reach the Manager management surfaces — Users, Teams,
 * Projects and Orgs. A user holding none of them is "role-less": once the View
 * Only floor is removed (PSTUDIO-4099) the nav hides these entries and the
 * routes are gated with this list. Super Admin and Organization Admin are the
 * "org admin" roles, Organization Manager the "manager" role named in Decision
 * 3 of the Manager Access Model. Logs stays admin-only — audit-logs keeps
 * [ADMIN, SUPER_ADMIN]. The user's own profile (My Account) is a separate,
 * always-reachable surface (PSTUDIO-4095) and is deliberately not gated.
 */
export const MANAGER_SURFACE_ROLES = [
  OrganizationRoleNames.ADMIN,
  OrganizationRoleNames.SUPER_ADMIN,
  OrganizationRoleNames.MANAGER,
];

export const useAuthRole = (authRoles: string[]) => {
  const { currentUser } = useUserCtxSelector((ctx) => ({
    currentUser: ctx.user,
  }));
  const { selectedOrgId } = useManagerCtxSelector((ctx) => ({
    selectedOrgId: ctx.selectedOrgId,
  }));

  // Consolidate all roles (both team-based and directly assigned) in a single operation
  const allUserRoles = [
    // Roles assigned through teams
    ...(currentUser?.assignedTeams?.flatMap(
      ({ assignedRoles, defaultAccount }) => {
        if (
          defaultAccount?.id === selectedOrgId ||
          defaultAccount?.allChildAccounts
            ?.map(({ id }) => id)
            ?.includes(selectedOrgId!)
        )
          return assignedRoles?.map(({ id, name }) => ({
            label: name,
            value: id,
          }));

        return [];
      }
    ) || []),

    // Directly assigned roles
    ...(currentUser?.assignedRoles?.map(({ name, id }) => ({
      label: name,
      value: id,
    })) || []),
  ];

  return allUserRoles.some((role) => authRoles.includes(role?.label as string));
};
